import { useId, type CSSProperties, type ReactNode } from 'react';
import type { ComboboxItem, SelectProps } from '@mantine/core';
import { useTheme } from '@contexts/ThemeContext';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { cn } from '@/lib/utils';
import {
  getJarvisControlStyle,
  jarvisControlClassName,
  renderFieldDescription,
  renderFieldError,
  renderFieldLabel,
  type JarvisFieldSize,
} from './jarvis-field-utils';

export type JarvisSelectProps = SelectProps;

function normalizeOptions(data: SelectProps['data']): ComboboxOption[] {
  if (!data) return [];
  return data.map((item, index) => {
    if (typeof item === 'string' || typeof item === 'number') {
      const value = String(item);
      return { value, label: value, searchLabel: value, key: value };
    }
    const option = item as ComboboxItem & { searchLabel?: string };
    const value = String(option.value);
    const label = option.label ?? value;
    return {
      value,
      label,
      // Rich (non-string) labels may carry their own plain search text.
      searchLabel: typeof label === 'string' ? label : option.searchLabel ?? value,
      disabled: option.disabled,
      key: `${value}-${index}`,
    };
  });
}

/**
 * JarvisSelect - Themed select wrapping the shadcn Combobox (Popover + Command).
 * Keeps the previous Mantine Select-compatible props API (data, searchable,
 * clearable, label, error, etc.) so call sites do not need to change.
 */
export const JarvisSelect = (props: JarvisSelectProps) => {
  const { colors: themeColors } = useTheme();
  const autoId = useId();

  const {
    label,
    description,
    error,
    size = 'sm',
    styles,
    className,
    style,
    required,
    withAsterisk,
    disabled,
    id,
    name,
    data,
    value,
    onChange,
    placeholder,
    searchable = false,
    clearable = false,
    nothingFoundMessage,
    searchValue,
    onSearchChange,
    onDropdownOpen,
    onDropdownClose,
    onFocus,
    onBlur,
    // Mantine-only props — strip so they never leak
    radius: _radius,
    variant: _variant,
    classNames: _classNames,
    unstyled: _unstyled,
    wrapperProps: _wrapperProps,
    inputWrapperOrder: _inputWrapperOrder,
    inputContainer: _inputContainer,
    labelProps: _labelProps,
    descriptionProps: _descriptionProps,
    errorProps: _errorProps,
    leftSection: _leftSection,
    rightSection: _rightSection,
    leftSectionWidth: _leftSectionWidth,
    rightSectionWidth: _rightSectionWidth,
    leftSectionPointerEvents: _leftSectionPointerEvents,
    rightSectionPointerEvents: _rightSectionPointerEvents,
    withErrorStyles: _withErrorStyles,
    allowDeselect: _allowDeselect,
    checkIconPosition: _checkIconPosition,
    comboboxProps: _comboboxProps,
    dropdownOpened: _dropdownOpened,
    filter: _filter,
    limit: _limit,
    maxDropdownHeight: _maxDropdownHeight,
    renderOption: _renderOption,
    selectFirstOptionOnChange: _selectFirstOptionOnChange,
    withCheckIcon: _withCheckIcon,
    withScrollArea: _withScrollArea,
    scrollAreaProps: _scrollAreaProps,
    defaultSearchValue: _defaultSearchValue,
    defaultValue: _defaultValue,
    readOnly: _readOnly,
    form: _form,
    ..._rest
  } = props as SelectProps & Record<string, unknown>;

  const fieldId = (id as string | undefined) ?? autoId;
  const hasError = Boolean(error);
  const options = normalizeOptions(data);

  const controlStyle = getJarvisControlStyle(themeColors, {
    hasError,
    styles,
    style: undefined,
  });

  // When label is omitted/empty, call sites often render JarvisInputLabel outside
  // (HeaderForm). renderFieldLabel already no-ops for empty labels so we never
  // paint a second required asterisk beside the Combobox trigger.
  const fieldLabel = renderFieldLabel({
    id: fieldId,
    label: label as ReactNode,
    required: required as boolean | undefined,
    withAsterisk: withAsterisk as boolean | undefined,
    textColor: themeColors.text,
  });

  return (
    <div className="w-full" style={style as CSSProperties | undefined}>
      {fieldLabel}

      <Combobox
        id={fieldId}
        name={name as string | undefined}
        options={options}
        value={(value as string | null | undefined) ?? null}
        onChange={(val) => {
          (onChange as ((v: string | null) => void) | undefined)?.(val);
        }}
        placeholder={(placeholder as string | undefined) ?? 'Select option...'}
        searchable={Boolean(searchable)}
        clearable={Boolean(clearable)}
        disabled={disabled as boolean | undefined}
        required={required as boolean | undefined}
        emptyText={typeof nothingFoundMessage === 'string' ? nothingFoundMessage : 'No results found.'}
        searchValue={searchValue as string | undefined}
        onSearchChange={onSearchChange as ((v: string) => void) | undefined}
        onOpenChange={(open) => {
          if (open) (onDropdownOpen as (() => void) | undefined)?.();
          else (onDropdownClose as (() => void) | undefined)?.();
        }}
        onFocus={onFocus as (() => void) | undefined}
        onBlur={onBlur as (() => void) | undefined}
        aria-invalid={hasError || undefined}
        triggerClassName={cn(
          jarvisControlClassName(size as JarvisFieldSize, className as string | undefined),
          'w-full justify-between'
        )}
        style={controlStyle}
      />

      {renderFieldDescription(description as ReactNode, themeColors.textSecondary)}
      {renderFieldError(error as ReactNode, themeColors.error)}
    </div>
  );
};

export default JarvisSelect;
